import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatAmount, getFormatConfig } from '../../../utils/global';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogCloseIconSx,
  harmonateSectionTitleSx,
  harmonateCancelButtonSx,
} from '../../../theme/harmonateStyles';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const getBankDisplayName = (bank) => bank?.bankShortName || bank?.bankName || bank?.bankCode || '';

const invoiceRemainingAmount = (invoice) => {
  if (invoice?.invoiceRemainingBalanceDue != null) {
    return Number(invoice.invoiceRemainingBalanceDue) || 0;
  }
  const total = Number(invoice?.invoiceTotalAmountDue) || 0;
  const paid = Number(invoice?.invoicePaymentsReceived) || 0;
  return Math.max(0, total - paid);
};

const buildBankSummaryRows = (paymentLines = []) => {
  const groups = new Map();

  paymentLines.forEach((line) => {
    const key = line.bankName || '—';
    const existing = groups.get(key) || {
      bankName: line.bankName || '—',
      paymentMethods: new Set(),
      paymentCount: 0,
      totalAmount: 0,
    };
    existing.paymentMethods.add(line.paymentMethod || '—');
    existing.paymentCount += 1;
    existing.totalAmount += Number(line.paymentAmount) || 0;
    groups.set(key, existing);
  });

  return Array.from(groups.values()).map((group) => ({
    bankName: group.bankName,
    paymentMethods: Array.from(group.paymentMethods).sort().join(', '),
    paymentCount: group.paymentCount,
    totalAmount: group.totalAmount,
  }));
};

const loadVendorInvoiceBankDetails = async (tenantId, vendorId, invoices = []) => {
  const invoiceById = Object.fromEntries(
    invoices.map((invoice) => [invoice.apInvoiceId, invoice]),
  );
  const invoiceIds = new Set(invoices.map((invoice) => invoice.apInvoiceId));

  const [transactions, banks] = await Promise.all([
    apiFetch(`conductor/payment-transactions/by-vendor?tenant_id=${tenantId}&vendor_id=${vendorId}`).catch(() => []),
    apiFetch(`conductor/cash-recon/banks/by-tenant-and-status?tenant_id=${tenantId}&status=ACTIVE`).catch(() => []),
  ]);

  const bankMap = Object.fromEntries(
    (Array.isArray(banks) ? banks : []).map((bank) => [bank.bankId, bank]),
  );
  const resolveBankName = (bankId) => getBankDisplayName(bankMap[bankId]) || '—';

  const paymentLines = [];

  await Promise.all(
    (Array.isArray(transactions) ? transactions : []).map(async (transaction) => {
      const mappings = await apiFetch(
        `conductor/invoice-payment-mappings/by-payment?tenant_id=${tenantId}&payment_id=${transaction.paymentId}`,
      ).catch(() => []);

      (Array.isArray(mappings) ? mappings : []).forEach((mapping) => {
        if (!invoiceIds.has(mapping.apInvoiceId)) return;

        const invoice = invoiceById[mapping.apInvoiceId];
        paymentLines.push({
          paymentId: transaction.paymentId,
          invoiceNumber: invoice?.invoiceNumber || '—',
          bankName: resolveBankName(transaction.bankId || mapping.bankId),
          paymentMethod: transaction.paymentMethod || '—',
          paymentAmount: Number(mapping.paidAmount ?? transaction.paymentAmount) || 0,
          paymentStatus: transaction.paymentStatus || '—',
          paymentReference: transaction.paymentReference || '—',
        });
      });
    }),
  );

  paymentLines.sort((a, b) => a.invoiceNumber.localeCompare(b.invoiceNumber));

  return {
    paymentLines,
    bankSummaryRows: buildBankSummaryRows(paymentLines),
  };
};

const VendorInvoiceBankDetailsDialog = ({
  open,
  onClose,
  tenantId,
  vendorId,
  vendorName,
  invoices = [],
}) => {
  const config = getFormatConfig();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !tenantId || !vendorId) {
      setDetail(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(null);
    setDetail(null);

    loadVendorInvoiceBankDetails(tenantId, vendorId, invoices)
      .then((loaded) => {
        if (active) setDetail(loaded);
      })
      .catch((err) => {
        if (active) setError(err.message || 'Failed to load bank details');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, tenantId, vendorId, invoices]);

  const bankCount = detail?.bankSummaryRows?.length || 0;
  const paymentCount = detail?.paymentLines?.length || 0;
  const invoicesWithoutPayments = useMemo(() => {
    if (!detail) return [];
    const paidInvoiceNumbers = new Set(detail.paymentLines.map((line) => line.invoiceNumber));
    return invoices.filter((invoice) => !paidInvoiceNumbers.has(invoice.invoiceNumber));
  }, [detail, invoices]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
      <DialogTitle sx={harmonateDialogTitleSx}>
        Invoice Bank Details — {vendorName || '—'}
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Invoices</Typography>
            <Typography variant="body2">{invoices.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Banks / Payments</Typography>
            <Typography variant="body2">
              {bankCount} bank{bankCount === 1 ? '' : 's'}, {paymentCount} payment{paymentCount === 1 ? '' : 's'}
            </Typography>
          </Box>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Typography variant="body2" color="error">{error}</Typography>
        )}

        {!loading && !error && detail && (
          <>
            <Box>
              <Typography variant="subtitle2" sx={{ ...harmonateSectionTitleSx, mb: 1 }}>Bank Summary</Typography>
              <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bank</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell align="right">Payments</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.bankSummaryRows.length ? detail.bankSummaryRows.map((row) => (
                      <TableRow key={`${row.bankName}-${row.paymentMethods}`}>
                        <TableCell>{row.bankName || '—'}</TableCell>
                        <TableCell>{row.paymentMethods || '—'}</TableCell>
                        <TableCell align="right">{row.paymentCount}</TableCell>
                        <TableCell align="right">{formatAmount(row.totalAmount, config.AmountFormat)}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            No payment bank details found for these invoices.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ ...harmonateSectionTitleSx, mb: 1 }}>Invoice Payments</Typography>
              <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Bank</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.paymentLines.length ? detail.paymentLines.map((line) => (
                      <TableRow key={`${line.paymentId}-${line.invoiceNumber}`}>
                        <TableCell>{line.invoiceNumber}</TableCell>
                        <TableCell>{line.bankName || '—'}</TableCell>
                        <TableCell>{line.paymentMethod}</TableCell>
                        <TableCell>{line.paymentStatus}</TableCell>
                        <TableCell align="right">{formatAmount(line.paymentAmount, config.AmountFormat)}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            No payments are linked to these invoices yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Box>

            {invoicesWithoutPayments.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ ...harmonateSectionTitleSx, mb: 1 }}>
                  Invoices Without Bank Assignment
                </Typography>
                <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                        <TableCell align="right">Remaining</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoicesWithoutPayments.map((invoice) => (
                        <TableRow key={invoice.apInvoiceId}>
                          <TableCell>{invoice.invoiceNumber || '—'}</TableCell>
                          <TableCell align="right">
                            {formatAmount(invoice.invoiceTotalAmountDue, config.AmountFormat)}
                          </TableCell>
                          <TableCell align="right">
                            {formatAmount(invoiceRemainingAmount(invoice), config.AmountFormat)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={harmonateCancelButtonSx}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorInvoiceBankDetailsDialog;
